import { useState } from "react";
import Link from "next/link";
import { ChevronDown } from "lucide-react";
import parse from "html-react-parser";
import styles from "./FaqSimple.module.css";
import { FAQItem } from "@/data/faq";

interface FaqSimpleProps {
  questionsAndAnswers: FAQItem[];
  noReadMore?: boolean;
}

interface FaqItemProps {
  item: FAQItem;
  index: number;
  isOpen: boolean;
  onToggle: () => void;
}

const FaqItemComponent = ({ item, index, isOpen, onToggle }: FaqItemProps) => {
  const answerId = `faq-answer-${index}`;
  const triggerId = `faq-trigger-${index}`;
  return (
    <div className={`mb-4 ${styles.item}`}>
      <div className={styles.accentBar} />
      <button
        id={triggerId}
        className={styles.trigger}
        onClick={onToggle}
        aria-expanded={isOpen}
        aria-controls={answerId}
      >
        <span className={styles.question}>{item.question}</span>
        <ChevronDown
          className={`${styles.chevron} ${isOpen ? styles.chevronOpen : ""}`}
        />
      </button>
      <div
        id={answerId}
        className={`${styles.answerWrapper} ${isOpen ? styles.answerWrapperOpen : ""}`}
        role="region"
        aria-labelledby={triggerId}
      >
        <div className={styles.answerInner}>
          <p className={styles.answer}>{parse(item.answer)}</p>
        </div>
      </div>
    </div>
  );
};

const FaqSimple = ({ questionsAndAnswers, noReadMore }: FaqSimpleProps) => {
  const [activeIndex, setActiveIndex] = useState(-1);

  const toggleActiveIndex = (index: number) => {
    setActiveIndex(activeIndex === index ? -1 : index);
  };

  return (
    <section className={styles.section}>
      <div className={styles.container}>
        <header className={styles.header}>
          <h2 className={styles.title}>Frequently Asked Questions</h2>
        </header>

        <div>
          {questionsAndAnswers.map((item, index) => (
            <div key={index}>
              <FaqItemComponent
                item={item}
                index={index}
                isOpen={activeIndex === index}
                onToggle={() => toggleActiveIndex(index)}
              />
            </div>
          ))}
        </div>

        {!noReadMore && (
          <div className={styles.readMore}>
            <Link href="/help/categories/faqs">
              <a className={styles.readMoreLink}>Read more FAQs</a>
            </Link>
          </div>
        )}
      </div>
    </section>
  );
};

export default FaqSimple;
